import type { FmpInsiderTradeRow } from '@/services/api';

/** SEC Form 4 transaction code prefix (e.g. S from "S-Sale"). */
export type InsiderTransactionCode = string;

export interface InsiderTransactionTypeDef {
  code: InsiderTransactionCode;
  label: string;
  hint: string;
  color: string;
}

/** Common Form 4 codes surfaced in the layer filter. */
export const INSIDER_TRANSACTION_TYPE_DEFS: InsiderTransactionTypeDef[] = [
  { code: 'S', label: 'S — Sale', hint: 'Open market or private sale', color: '#ef4444' },
  { code: 'P', label: 'P — Purchase', hint: 'Open market or private purchase', color: '#10b981' },
  { code: 'A', label: 'A — Award', hint: 'Grant, award, or other acquisition', color: '#6366f1' },
  { code: 'M', label: 'M — Exempt', hint: 'Exercise or conversion exempt from §16(b)', color: '#0ea5e9' },
  { code: 'C', label: 'C — Conversion', hint: 'Conversion of derivative security', color: '#f59e0b' },
  { code: 'G', label: 'G — Gift', hint: 'Bona fide gift', color: '#a855f7' },
  { code: 'F', label: 'F — In-kind', hint: 'Payment of exercise price or tax', color: '#64748b' },
  { code: 'X', label: 'X — In-the-money', hint: 'Exercise of in-the-money derivative', color: '#78716c' },
  { code: 'D', label: 'D — To issuer', hint: 'Disposition back to issuer', color: '#94a3b8' },
  { code: 'J', label: 'J — Other', hint: 'Other acquisition or disposition', color: '#6b7280' },
];

export const INSIDER_TRADING_ENABLED_TYPE_CODES_KEY = 'insiderTradingEnabledTypeCodes';

const DEFAULT_ENABLED_CODES = INSIDER_TRANSACTION_TYPE_DEFS.map((d) => d.code);

export function insiderTransactionCode(trade: FmpInsiderTradeRow): InsiderTransactionCode {
  const tt = (trade.transactionType ?? '').trim();
  const m = tt.match(/^([A-Z])/i);
  if (m) return m[1]!.toUpperCase();
  const ad = (trade.acquisitionOrDisposition ?? '').trim().toUpperCase();
  if (ad === 'A') return 'P';
  if (ad === 'D') return 'S';
  return '?';
}

export function insiderTransactionTypeLabel(trade: FmpInsiderTradeRow): string {
  const tt = (trade.transactionType ?? '').trim();
  if (tt) return tt;
  const code = insiderTransactionCode(trade);
  const def = INSIDER_TRANSACTION_TYPE_DEFS.find((d) => d.code === code);
  return def?.label ?? code;
}

export function insiderTradeDiamondColorForCode(code: InsiderTransactionCode): string {
  const def = INSIDER_TRANSACTION_TYPE_DEFS.find((d) => d.code === code);
  if (def) return def.color;
  return '#6b7280';
}

export function insiderTradeDiamondColor(trade: FmpInsiderTradeRow): string {
  return insiderTradeDiamondColorForCode(insiderTransactionCode(trade));
}

export function readInsiderTradingEnabledTypeCodes(): Set<InsiderTransactionCode> {
  if (typeof window === 'undefined') return new Set(DEFAULT_ENABLED_CODES);
  try {
    const raw = window.localStorage.getItem(INSIDER_TRADING_ENABLED_TYPE_CODES_KEY);
    if (!raw) return new Set(DEFAULT_ENABLED_CODES);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set(DEFAULT_ENABLED_CODES);
    const codes = parsed.filter((c): c is string => typeof c === 'string' && c.length > 0);
    return codes.length > 0 ? new Set(codes.map((c) => c.toUpperCase())) : new Set(DEFAULT_ENABLED_CODES);
  } catch {
    return new Set(DEFAULT_ENABLED_CODES);
  }
}

export function persistInsiderTradingEnabledTypeCodes(codes: ReadonlySet<InsiderTransactionCode>): void {
  try {
    window.localStorage.setItem(
      INSIDER_TRADING_ENABLED_TYPE_CODES_KEY,
      JSON.stringify([...codes])
    );
  } catch {
    /* ignore */
  }
}

export function tradeMatchesEnabledTypeCodes(
  trade: FmpInsiderTradeRow,
  enabledCodes: ReadonlySet<InsiderTransactionCode>
): boolean {
  if (enabledCodes.size === 0) return false;
  return enabledCodes.has(insiderTransactionCode(trade));
}

export function countInsiderTradesByTypeCode(
  trades: FmpInsiderTradeRow[] | undefined
): Map<InsiderTransactionCode, number> {
  const counts = new Map<InsiderTransactionCode, number>();
  if (!trades?.length) return counts;
  for (const t of trades) {
    const code = insiderTransactionCode(t);
    counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return counts;
}
